import React, { useEffect, useState } from "react";
import {
    Box,
    Flex,
    Text,
    Icon,
    Divider,
    Stack,
    Switch,
} from "@chakra-ui/react";

import {
    Calendar,
    CalendarCheck,
    Percent,
    CircleCheck,
} from "lucide-react";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import GradientButton from "components/Buttons/GradientButton";
import ReusableInput from "components/Inputs/ReusableInput";

import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

import { useDispatch } from "react-redux";

import {
    updateCommissionConfigurationAction,
    fetchCommissionConfigurationsAction,
} from "state/actions/agentCommissionActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";
import { formatDate } from "utils/dateFormatter";

const ViewCommissionConfiguration = ({
    isOpen,
    onClose,
    configuration,
    startInEditMode = false,
}) => {
    const dispatch = useDispatch();

    const showToast = useCustomNotification();

    const [isEditing, setIsEditing] = useState(
        startInEditMode
    );

    const [loading, setLoading] = useState(false);

    const [localConfiguration, setLocalConfiguration] =
        useState(configuration);

    const [formValues, setFormValues] = useState({
        commission_percentage: 0,
        is_active: true,
    });

    useEffect(() => {
        if (configuration) {
            setLocalConfiguration(configuration);

            setFormValues({
                commission_percentage:
                    configuration.commission_percentage ?? 0,
                is_active:
                    configuration.is_active ?? true,
            });
        }

        setIsEditing(startInEditMode);
    }, [configuration, startInEditMode]);

    const handleInputChange = (field, value) => {
        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleToggleEdit = async () => {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        if (!localConfiguration?.configuration_id) {
            return;
        }

        const percentage = Number(
            formValues.commission_percentage
        );

        if (
            Number.isNaN(percentage) ||
            percentage < 0 ||
            percentage > 100
        ) {
            showToast({
                message:
                    "Invalid commission percentage.",
                description:
                    "Commission percentage must be between 0 and 100.",
                type: "error",
                duration: 3,
            });

            return;
        }

        setLoading(true);

        try {
            const payload = {
                commission_percentage: percentage,
                is_active: formValues.is_active,
            };

            const response = await dispatch(
                updateCommissionConfigurationAction(
                    localConfiguration.configuration_id,
                    payload
                )
            );

            if (response?.success) {
                setLocalConfiguration((prev) => ({
                    ...prev,
                    ...payload,
                    updated_at:
                        new Date().toISOString(),
                }));

                showToast({
                    message:
                        "Commission configuration updated successfully!",
                    type: "success",
                    duration: 3,
                });

                dispatch(
                    fetchCommissionConfigurationsAction()
                );
            } else {
                throw new Error(
                    response?.error ||
                        "Failed to update commission configuration."
                );
            }
        } catch (error) {
            showToast({
                message:
                    "Failed to update commission configuration.",
                description: error.message,
                type: "error",
                duration: 3,
            });

            setFormValues({
                commission_percentage:
                    localConfiguration?.commission_percentage ??
                    0,
                is_active:
                    localConfiguration?.is_active ?? true,
            });
        } finally {
            setLoading(false);
            setIsEditing(false);
        }
    };

    const handleCloseDrawer = () => {
        setIsEditing(false);
        onClose();
    };

    const handleCancelEdit = () => {
        setFormValues({
            commission_percentage:
                localConfiguration?.commission_percentage ?? 0,
            is_active:
                localConfiguration?.is_active ?? true,
        });

        setIsEditing(false);
    };

    if (!localConfiguration) {
        return null;
    }

    const status = localConfiguration.is_active
        ? "Active"
        : "Inactive";

    return (
        <Flex
            direction="column"
            p={8}
            width="100%"
        >
            <ReusableDrawer
                isOpen={isOpen}
                onClose={handleCloseDrawer}
                headerContent={
                    <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Text
                            fontSize="lg"
                            fontWeight="bold"
                        >
                            Commission Configuration
                        </Text>
                    </Flex>
                }
                bodyContent={
                    <Box mt={4}>
                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Text
                                fontSize="2xl"
                                fontWeight="bold"
                            >
                                Agent Commission
                            </Text>

                            <StatusBadge
                                label={status}
                                variant={getStatusVariant(
                                    status
                                )}
                            />
                        </Flex>

                        <Flex
                            mt={2}
                            alignItems="center"
                        >
                            <Icon
                                as={Calendar}
                                size={16}
                                color="gray.500"
                                mr={1}
                            />

                            <Text
                                fontSize="sm"
                                color="gray.500"
                                mr={4}
                            >
                                Created At:{" "}
                                {formatDate(
                                    localConfiguration.created_at
                                )}
                            </Text>
                        </Flex>

                        <Flex
                            mt={2}
                            alignItems="center"
                        >
                            <Icon
                                as={CalendarCheck}
                                size={16}
                                color="gray.500"
                                mr={1}
                            />

                            <Text
                                fontSize="sm"
                                color="gray.500"
                                mr={4}
                            >
                                Updated At:{" "}
                                {formatDate(
                                    localConfiguration.updated_at
                                )}
                            </Text>
                        </Flex>

                        <Divider my={6} />

                        <Flex
                            direction="column"
                            p={4}
                        >
                            <Text
                                fontSize="lg"
                                fontWeight="bold"
                                mb={2}
                            >
                                Commission Details
                            </Text>

                            <Stack spacing={4}>
                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >
                                    <Icon
                                        as={Percent}
                                        mr={2}
                                        color="gray.400"
                                    />

                                    {isEditing ? (
                                        <ReusableInput
                                            label="Commission Percentage"
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={
                                                formValues.commission_percentage
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "commission_percentage",
                                                    e.target.value
                                                )
                                            }
                                            width="100%"
                                        />
                                    ) : (
                                        <Text
                                            fontWeight="500"
                                            color="gray.500"
                                            fontSize="sm"
                                        >
                                            Commission Percentage:{" "}
                                            {
                                                localConfiguration.commission_percentage
                                            }
                                            %
                                        </Text>
                                    )}
                                </Flex>

                                <Flex
                                    alignItems="center"
                                    justifyContent="space-between"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >
                                    <Flex alignItems="center">
                                        <Icon
                                            as={CircleCheck}
                                            mr={2}
                                            color="gray.400"
                                        />

                                        <Text
                                            fontWeight="500"
                                            color="gray.500"
                                            fontSize="sm"
                                        >
                                            Status
                                        </Text>
                                    </Flex>

                                    {isEditing ? (
                                        <Switch
                                            isChecked={
                                                formValues.is_active
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "is_active",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    ) : (
                                        <StatusBadge
                                            label={status}
                                            variant={getStatusVariant(
                                                status
                                            )}
                                        />
                                    )}
                                </Flex>

                               
                            </Stack>

                            <Flex
                                justifyContent="flex-end"
                                gap={3}
                                mt={6}
                            >
                                {isEditing && (
                                    <GradientButton
                                        height="30px"
                                        fontSize="sm"
                                        onClick={
                                            handleCancelEdit
                                        }
                                        isDisabled={loading}
                                       
                                    >
                                        Cancel
                                    </GradientButton>
                                )}

                                <GradientButton
                                    height="30px"
                                    fontSize="sm"
                                    onClick={handleToggleEdit}
                                    isLoading={loading}
                                    
                                >
                                    {isEditing
                                        ? "Save"
                                        : "Edit"}
                                </GradientButton>
                            </Flex>
                        </Flex>
                    </Box>
                }
            />
        </Flex>
    );
};

export default ViewCommissionConfiguration;